import { NgModule } from "@angular/core";
import { RouterModule } from "@angular/router";
import { NextPageRoute } from "@cvx/nextpage";
import { HomeComponent } from "./home/home.component";
import { ContactComponent } from "./help/contact/contact.component";
import { HelpComponent } from "./help/help/help.component";
import { TurbineListComponent } from "./turbines/turbine-list.component";
import { AssetHierarchyComponent } from "./asset-hierarchy/asset-hierarchy.component";
import { RamTargetsComponent } from "./ram-targets/ram-targets.component";
import { TechnicalLettersComponent } from "./technical-letters/technical-letters.component";
import { PageConfig } from "./page-config";
import { RoutesService } from "./services/routes.service";
import { RoutePermissionService } from "./services/route-permission.service";
import { NoAccessComponent } from "./shared/no-access/no-access.component";
import { CalGuardService } from "@cvx/cal-angular";
import { TurbineCreateComponent } from "./turbines/create/turbine-create.component";
import { TurbineEditComponent } from "./turbines/edit/turbine-edit.component";
import { ManageIssuersComponent } from "./technical-letters/manage-issuers/manage-issuers.component";
import { AddLetterComponent } from "./technical-letters/add-letter/add-letter.component";
import { PlantAdminsComponent } from "./technical-letters/plant-admins/plant-admins.component";
import { DeletedLetterLogsComponent } from "./technical-letters/deleted-letter-logs/deleted-letter-logs.component";
import { EditTechnicalLettersComponent } from "./technical-letters/edit-technical-letters/edit-technical-letters.component";
import { GroupAccessGuard } from "./guards/group-access.guard";
import { GroupAccessConfig } from "./interfaces/common/group-access-config.interface";
import { AddEquipmentComponent } from "./technical-letters/add-equipment/add-equipment.component";
import { DeletedEquipmentLogComponent } from "./technical-letters/deleted-equipment-log/deleted-equipment-log.component";
import { HistoryLogComponent } from "./technical-letters/history-log/history-log.component";
import { LetterLogComponent } from "./technical-letters/letter-log/letter-log.component";
import { BulkEditComponent } from "./technical-letters/bulk-edit/bulk-edit.component";
import { LetterEquipmentDocComponent } from "./technical-letters/letter-equipment-doc/letter-equipment-doc.component";

let routes: NextPageRoute[] = [
  {
    path: "home",
    title: "Home",
    redirectTo: "/",
    pathMatch: "full",
  },
  {
    path: "",
    title: "Main",
    children: [
      {
        path: "",
        component: HomeComponent,
        canActivate: [CalGuardService],
      },
    ],
  },
  {
    path: "turbines",
    title: "Turbines",
    canActivate: [CalGuardService, GroupAccessGuard],
    data: GroupAccessConfig.turbineUsers(),
    children: [
      {
        path: "",
        title: "Turbine List",
        component: TurbineListComponent,
        canActivate: [CalGuardService, GroupAccessGuard],
        data: GroupAccessConfig.turbineUsers(),
      },
      {
        path: "create",
        component: TurbineCreateComponent,
        canActivate: [CalGuardService, GroupAccessGuard],
        data: GroupAccessConfig.turbineUsers(),
      },
      {
        path: "edit/:id",
        component: TurbineEditComponent,
        canActivate: [CalGuardService, GroupAccessGuard],
        data: GroupAccessConfig.turbineUsers(),
      },
    ],
  },
  {
    path: "asset-hierarchy",
    title: "Asset Hierarchy",
    component: AssetHierarchyComponent,
    canActivate: [CalGuardService, GroupAccessGuard],
    data: GroupAccessConfig.turbineUsers(),
  },
  {
    path: "ram-targets",
    title: "RAM Targets",
    component: RamTargetsComponent,
    canActivate: [CalGuardService, GroupAccessGuard],
    data: GroupAccessConfig.ramPlanners(),
  },
  {
    path: "technical-letters",
    title: "Service Bulletins",
    canActivate: [CalGuardService, GroupAccessGuard],
    data: GroupAccessConfig.technicalLetterViewers(),
    children: [
      {
        path: "",
        title: "Service Bulletins",
        component: TechnicalLettersComponent,
        canActivate: [CalGuardService],
        data: GroupAccessConfig.technicalLetterViewers(),
      },
      {
        path: "manage-issuers",
        component: ManageIssuersComponent,
        canActivate: [CalGuardService, GroupAccessGuard],
        data: GroupAccessConfig.technicalLetterViewers(),
      },
      {
        path: "create",
        component: AddLetterComponent,
        canActivate: [CalGuardService, GroupAccessGuard],
        data: GroupAccessConfig.technicalLetterViewers(),
      },
      {
        path: "bulk-edit",
        component: BulkEditComponent,
        canActivate: [CalGuardService],
      },
      {
        path: "plant-admins",
        component: PlantAdminsComponent,
        canActivate: [CalGuardService, GroupAccessGuard],
        data: GroupAccessConfig.technicalLetterViewers(),
      },
      {
        path: "deleted-letter-logs",
        component: DeletedLetterLogsComponent,
        canActivate: [CalGuardService, GroupAccessGuard],
        data: GroupAccessConfig.technicalLetterViewers(),
      },
      {
        path: "edit-technical-letters/:id",
        component: EditTechnicalLettersComponent,
        canActivate: [CalGuardService, GroupAccessGuard],
        data: GroupAccessConfig.technicalLetterViewers(),
      },
      {
        path: "add-equipment/:letterId",
        component: AddEquipmentComponent,
        canActivate: [CalGuardService],
        data: PageConfig.UserReadOnly,
      },

      {
        path: "history-log/:id",
        component: HistoryLogComponent,
        canActivate: [CalGuardService],
        data: PageConfig.UserReadOnly,
      },
      {
        path: "letter-history/:letterId",
        component: LetterLogComponent,
        canActivate: [CalGuardService],
        data: PageConfig.UserReadOnly,
      },
      {
        path: "deleted-equipment-log/:letterId",
        component: DeletedEquipmentLogComponent,
        canActivate: [CalGuardService],
        data: PageConfig.UserReadOnly,
      },
      {
        path: "letter-equipment-doc/:id",
        component: LetterEquipmentDocComponent,
        canActivate: [CalGuardService],
        data: PageConfig.UserReadOnly,
      },
    ],
  },
  {
    path: "help",
    title: "Help",
    canActivate: [CalGuardService],
    children: [
      {
        path: "",
        title: "Help",
        redirectTo: "main",
        pathMatch: "full",
      },
      {
        path: "main",
        title: "Help",
        component: HelpComponent,
        canActivate: [CalGuardService],
      },
      {
        path: "contact-us",
        title: "Contact",
        component: ContactComponent,
        canActivate: [CalGuardService],
      },
    ],
  },
  { path: "no-access", component: NoAccessComponent },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {
  constructor(
    private routesService: RoutesService,
    private routePermissionService: RoutePermissionService
  ) {
    // Initialize routes with group-based filtering
    this.routePermissionService.initializeRoutes(routes);
  }
}
